import React, {useEffect, useState} from "react";
import {Button, Col, Form, Modal, Row, Table} from "react-bootstrap";
import {useAlert} from "react-alert";
import {Loading} from "../../../genericComponents";
import SimpleUpload from "../../../genericComponents/upload/SimpleUpload";
import {
  getDocumentTemplateById,
  getSubstitutionFields,
  insertDocumentTemplate,
  previewDocumentTemplate,
  updateDocumentTemplate,
} from "../../../services/RequestService";
import {fn} from "../../../utils";
import location from "../../../utils/location";
import CboAutoComplete from "../../InputAutoComplete/CboAutoComplete";
import "./ModalDocumentGenerator.css";

const FIELD_TYPES = [
  { value: "TEXT", label: "Texto" },
  { value: "EMAIL", label: "E-mail" },
  { value: "PHONE", label: "Telefone" },
  { value: "CPF", label: "CPF" },
  { value: "DATE", label: "Data" },
  { value: "MONEY", label: "Moeda" },
  { value: "BOOLEAN", label: "Sim/Não" },
  { value: "UF", label: "UF (Estado)" },
  { value: "CURRENT_DATE", label: "Data atual" },
  { value: "CBO", label: "CBO" },
];

const EMPTY_VARIABLE = {
  description: "",
  variable: "",
  type: "TEXT",
  sourceField: "USER_FILLED",
  previewValue: "",
};

export default function ModalDocumentGenerator({ show, onHide, data }) {
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const [substitutionFields, setSubstitutionFields] = useState([]);
  const [form, setForm] = useState({ id: null, description: "", fileBase64: "", variables: [] });

  useEffect(() => {
    getSubstitutionFields().then(({ ok, data }) => {
      if (ok) setSubstitutionFields(data);
    });
    if (data?.id) {
      setLoading(true);
      getDocumentTemplateById(data.id).then(({ ok, data }) => {
        if (ok) {
          setForm({
            id: data.id,
            description: data.description || "",
            fileBase64: data.fileBase64 || "",
            variables: data.variables || [],
          });
        }
        setLoading(false);
      });
    }
    // eslint-disable-next-line
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const setVariable = (index, key, value) => {
    setForm((f) => {
      const variables = [...f.variables];
      variables[index] = { ...variables[index], [key]: value };
      return { ...f, variables };
    });
  };

  const addVariable = () => setField("variables", [...form.variables, { ...EMPTY_VARIABLE }]);

  const removeVariable = (index) =>
    setField("variables", form.variables.filter((_, i) => i !== index));

  const isValid = () =>
    form.description.trim() !== "" &&
    form.fileBase64 !== "" &&
    form.variables.every((v) => /^[A-Za-z0-9_]+$/.test(v.variable || ""));

  async function save() {
    setLoading(true);
    const request = form.id ? updateDocumentTemplate : insertDocumentTemplate;
    const { ok, data } = await request(form);
    if (ok) {
      alert.show("Modelo salvo com sucesso", { type: "success" });
      onHide();
    } else {
      alert.show(data?.log || "Erro ao salvar o modelo", { type: "error" });
    }
    setLoading(false);
  }

  async function preview() {
    setLoading(true);
    const values = {};
    form.variables.forEach((v) => {
      values[v.variable] = v.previewValue || "";
    });
    const { ok, data } = await previewDocumentTemplate({ fileBase64: form.fileBase64, values });
    if (ok) {
      fn.downloadBase64File(data.base64, data.fileName, fn.DOCX_MIME);
    } else {
      alert.show(data?.log || "Erro ao gerar a visualização", { type: "error" });
    }
    setLoading(false);
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static" scrollable dialogClassName="doc-generator-modal">
      <Modal.Header closeButton>
        <Modal.Title>{form.id ? "Editar Modelo de Documento" : "Novo Modelo de Documento"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Loading loading={loading}>
          <Row>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Descrição *</Form.Label>
                <Form.Control
                  value={form.description}
                  className={form.description.trim() === "" ? "is-invalid" : "is-valid"}
                  onChange={(e) => setField("description", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Modelo (.docx) *</Form.Label>
                <SimpleUpload
                  accept=".docx"
                  value={form.fileBase64}
                  onUpload={(b64) => setField("fileBase64", b64)}
                />
                <small className="text-muted">
                  {"Dentro do documento use as variáveis no formato ${variavel}, ex.: ${nome}, ${endereco}"}
                </small>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex align-items-center justify-content-between mt-2 mb-2">
            <b>Variáveis do modelo</b>
            <Button variant="primary" size="sm" onClick={addVariable}>
              <i className="fas fa-plus-circle mr-1" />
              Adicionar variável
            </Button>
          </div>
          <Table bordered size="sm">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Variável</th>
                <th style={{ width: "12%" }}>Tipo</th>
                <th style={{ width: "22%" }}>Campo substituição</th>
                <th>Valor de visualização</th>
                <th style={{ width: "40px" }}></th>
              </tr>
            </thead>
            <tbody>
              {form.variables.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    Nenhuma variável cadastrada
                  </td>
                </tr>
              )}
              {form.variables.map((v, i) => (
                <tr key={i}>
                  <td>
                    <Form.Control
                      size="sm"
                      value={v.description || ""}
                      placeholder="Rótulo exibido na tela"
                      onChange={(e) => setVariable(i, "description", e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      value={v.variable || ""}
                      placeholder="ex.: nome"
                      className={/^[A-Za-z0-9_]+$/.test(v.variable || "") ? "is-valid" : "is-invalid"}
                      onChange={(e) => setVariable(i, "variable", e.target.value.trim())}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      as="select"
                      value={v.type || "TEXT"}
                      onChange={(e) => setVariable(i, "type", e.target.value)}
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Form.Control>
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      as="select"
                      value={v.sourceField || "USER_FILLED"}
                      onChange={(e) => setVariable(i, "sourceField", e.target.value)}
                    >
                      {substitutionFields.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </Form.Control>
                  </td>
                  <td>
                    {v.type === "BOOLEAN" ? (
                      <Form.Control
                        size="sm"
                        as="select"
                        value={v.previewValue || ""}
                        onChange={(e) => setVariable(i, "previewValue", e.target.value)}
                      >
                        <option value="">Selecione...</option>
                        <option value="Sim">Sim</option>
                        <option value="Não">Não</option>
                      </Form.Control>
                    ) : v.type === "UF" ? (
                      <Form.Control
                        size="sm"
                        as="select"
                        value={v.previewValue || ""}
                        onChange={(e) => setVariable(i, "previewValue", e.target.value)}
                      >
                        <option value="">Selecione...</option>
                        {location.states.map((uf) => (
                          <option key={uf.value} value={uf.value}>
                            {uf.label}
                          </option>
                        ))}
                      </Form.Control>
                    ) : v.type === "CBO" ? (
                      <CboAutoComplete
                        value={v.previewValue || null}
                        onChange={(descricao) =>
                          setVariable(i, "previewValue", descricao || "")
                        }
                      />
                    ) : (
                      <Form.Control
                        size="sm"
                        value={v.previewValue || ""}
                        placeholder="Valor de teste"
                        onChange={(e) => setVariable(i, "previewValue", e.target.value)}
                      />
                    )}
                  </td>
                  <td className="text-center align-middle">
                    <i
                      className="fas fa-trash-alt is-required"
                      style={{ cursor: "pointer" }}
                      title="Remover variável"
                      onClick={() => removeVariable(i)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Loading>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="info" disabled={!form.fileBase64 || loading} onClick={preview}>
          <i className="fas fa-eye mr-1" />
          Visualizar
        </Button>
        <Button variant="success" disabled={!isValid() || loading} onClick={save}>
          Salvar
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
